import { useState, useEffect } from 'react';
import {
  Typography,
  Card,
  Form,
  Input,
  Button,
  Space,
  message,
  Descriptions,
  Alert,
  List,
  Modal,
} from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { api } from '../lib/api';
import type { AccountDeletionStatus, User } from '../types';

const { Title } = Typography;

export default function ProfilePage() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [changing, setChanging] = useState(false);
  const [deleteStatus, setDeleteStatus] = useState<AccountDeletionStatus | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    loadUser();
  }, []);

  const loadUser = async () => {
    try {
      const [userData, deletionStatus] = await Promise.all([
        api.auth.getSession(),
        api.user.accountDeletionStatus().catch(() => null),
      ]);
      setUser(userData);
      setDeleteStatus(deletionStatus);
    } catch (error: any) {
      message.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (values: {
    currentPassword: string;
    newPassword: string;
  }) => {
    try {
      setChanging(true);
      await api.auth.changePassword(values.currentPassword, values.newPassword);
      message.success('Password changed successfully');
      form.resetFields();
    } catch (error: any) {
      message.error(error.message || 'Failed to change password');
    } finally {
      setChanging(false);
    }
  };

  const handleDeleteAccount = () => {
    if (!deleteStatus?.canDelete) {
      message.error('Cancel active paid subscriptions before deleting your account.');
      return;
    }

    Modal.confirm({
      title: 'Delete account permanently?',
      okText: 'Delete Account',
      okType: 'danger',
      cancelText: 'Keep Account',
      content: (
        <Space direction="vertical">
          <Typography.Text>
            This action is permanent. Your profile, access, and organization memberships will be removed.
          </Typography.Text>
          {deleteStatus.autoCancelableTrials.length > 0 ? (
            <Typography.Text type="secondary">
              Trial subscriptions will be canceled automatically.
            </Typography.Text>
          ) : null}
          <Typography.Text type="secondary">
            Your email will remain reserved for trial history.
          </Typography.Text>
        </Space>
      ),
      onOk: async () => {
        try {
          setDeleting(true);
          await api.user.deleteAccount();
          message.success('Account deleted');
          window.location.href = '/login';
        } catch (error: any) {
          message.error(error.message || 'Failed to delete account');
          throw error;
        } finally {
          setDeleting(false);
        }
      },
    });
  };

  return (
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Title level={2}>Profile Settings</Title>

        <Card title="Account Information" loading={loading}>
          {user && (
            <Descriptions column={1} bordered>
              <Descriptions.Item label="Email">{user.email}</Descriptions.Item>
              <Descriptions.Item label="Name">{user.name || '—'}</Descriptions.Item>
              <Descriptions.Item label="Email Verified">
                {user.emailVerified ? '✓ Verified' : '✗ Not verified'}
              </Descriptions.Item>
              <Descriptions.Item label="Member Since">
                {new Date(user.createdAt).toLocaleDateString()}
              </Descriptions.Item>
            </Descriptions>
          )}
        </Card>

        <Card title="Change Password">
          <Form
            form={form}
            name="change-password"
            onFinish={handleChangePassword}
            layout="vertical"
            style={{ maxWidth: 400 }}
            requiredMark={false}
          >
            <Form.Item
              name="currentPassword"
              label="Current Password"
              rules={[
                { required: true, message: 'Please enter your current password' },
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="Current password"
              />
            </Form.Item>

            <Form.Item
              name="newPassword"
              label="New Password"
              rules={[
                { required: true, message: 'Please enter your new password' },
                { min: 6, message: 'Password must be at least 6 characters' },
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="New password (min 6 characters)"
              />
            </Form.Item>

            <Form.Item
              name="confirmPassword"
              label="Confirm New Password"
              dependencies={['newPassword']}
              rules={[
                { required: true, message: 'Please confirm your new password' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('newPassword') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Passwords do not match'));
                  },
                }),
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="Confirm new password"
              />
            </Form.Item>

            <Form.Item>
              <Button type="primary" htmlType="submit" loading={changing}>
                Change Password
              </Button>
            </Form.Item>
          </Form>
        </Card>

        <Card title="Delete Account">
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            {deleteStatus?.blockingSubscriptions?.length ? (
              <Alert
                type="warning"
                showIcon
                message="Account deletion is blocked"
                description="You have active paid subscriptions. Cancel them first on the Subscriptions page."
              />
            ) : (
              <Alert
                type="info"
                showIcon
                message="Delete account permanently"
                description="Trial subscriptions will be canceled automatically. Paid subscriptions must be canceled first."
              />
            )}

            {deleteStatus?.blockingSubscriptions?.length ? (
              <List
                size="small"
                bordered
                dataSource={deleteStatus.blockingSubscriptions}
                renderItem={(item) => (
                  <List.Item>
                    <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                      <Typography.Text>{item.appName}</Typography.Text>
                      <Typography.Text type="secondary">{item.status}</Typography.Text>
                    </Space>
                  </List.Item>
                )}
              />
            ) : null}

            <Space>
              <Button
                danger
                onClick={handleDeleteAccount}
                disabled={!deleteStatus?.canDelete || deleting}
                loading={deleting}
              >
                Delete Account
              </Button>
              {deleteStatus?.blockingSubscriptions?.length ? (
                <Button onClick={() => (window.location.href = '/subscriptions')}>
                  Manage Subscriptions
                </Button>
              ) : null}
            </Space>
          </Space>
        </Card>
      </Space>
  );
}
